import { X, Copy, Check, ChevronDown, ChevronUp, RotateCcw, Loader2 } from 'lucide-react';
import { useState, useMemo, useCallback } from 'react';
import type { CampaignWithComputed } from '../types/campaign';
import { formatEuro } from '../utils/currency';
import { useLanguage } from '../contexts/LanguageContext';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { useColumnResize } from '../hooks/useColumnResize';
import type { ColumnWidths } from '../hooks/useUserPreferences';
import { getInvoiceStats, getOverdueInvoices } from '../utils/paymentCalculations';
import { getExpensesTotalFor } from '../utils/paymentEmail';
import { getPayoutDetail, buildCreatorPayoutEmail, type PayoutDetail } from '../utils/creatorPayout';
import { daysOverdue } from '../utils/dates';
import { log } from '../lib/logger';

interface KpiModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  campaigns: CampaignWithComputed[];
  /** Projects listed above the campaigns (they don't fit the campaign table). */
  projectos?: { id: string; projecto_id: string; label: string; amount: number }[];
  onOpenProjecto?: (projectoId: string) => void;
  onEdit?: (campaign: CampaignWithComputed) => void;
  onMarkAsPaid?: (campaignId: string, amount: number, creatorName?: string) => Promise<void>;
}

interface CampaignPaymentInfo {
  campaign: CampaignWithComputed;
  totalReceived: number;
  netPayable: number;
  paidInvoices: number;
  totalInvoices: number;
  /** Which tranches, which months, how much — what the creator reads */
  detail: PayoutDetail;
}

interface InfluencerPayments {
  influencer: string;
  campaignInfos: CampaignPaymentInfo[];
  totalAmount: number;
}

const OVERDUE_COLUMN_WIDTHS: ColumnWidths = {
  overdueWorkCode: 85,
  overdueCampaignRef: 110,
  overdueInvoiceNumber: 95,
  overdueClient: 160,
  overdueBrand: 100,
  overdueDueDate: 95,
  overdueAmount: 95,
  overdueDaysOverdue: 100,
};

interface ResizeHandleProps {
  columnKey: string;
  onResizeStart: (columnKey: string, startX: number) => void;
  isResizing: boolean;
}

function ResizeHandle({ columnKey, onResizeStart, isResizing }: ResizeHandleProps) {
  return (
    <div
      className={`absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-brand-lime ${
        isResizing ? 'bg-brand-ink' : 'bg-transparent'
      } transition-colors group-hover:bg-brand-lime/60`}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onResizeStart(columnKey, e.clientX);
      }}
      style={{ userSelect: 'none' }}
    />
  );
}

export function KpiModal({ isOpen, onClose, title, campaigns, projectos = [], onOpenProjecto, onEdit, onMarkAsPaid }: KpiModalProps) {
  const { t } = useLanguage();
  const [copiedInfluencer, setCopiedInfluencer] = useState<string | null>(null);
  const [expandedInfluencers, setExpandedInfluencers] = useState<Set<string>>(new Set());
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [selectedCampaigns, setSelectedCampaigns] = useState<Map<string, Set<string>>>(new Map());
  const { preferences, updateColumnWidths } = useUserPreferences();

  const savedOverdueWidths = useMemo(() => {
    const allWidths = preferences?.table_column_widths;
    if (!allWidths) return {};
    const overdueWidths: ColumnWidths = {};
    Object.entries(allWidths).forEach(([key, value]) => {
      if (key.startsWith('overdue')) {
        overdueWidths[key] = value;
      }
    });
    return overdueWidths;
  }, [preferences?.table_column_widths]);

  const handleSaveWidths = useCallback((newOverdueWidths: ColumnWidths) => {
    const currentWidths = preferences?.table_column_widths || {};
    const nonOverdueWidths: ColumnWidths = {};
    Object.entries(currentWidths).forEach(([key, value]) => {
      if (!key.startsWith('overdue')) {
        nonOverdueWidths[key] = value;
      }
    });
    updateColumnWidths({ ...nonOverdueWidths, ...newOverdueWidths });
  }, [preferences?.table_column_widths, updateColumnWidths]);

  const {
    columnWidths: overdueColumnWidths,
    isResizing,
    resizingColumn,
    handleResizeStart,
    getColumnStyle,
    resetWidths,
  } = useColumnResize({
    defaultWidths: OVERDUE_COLUMN_WIDTHS,
    savedWidths: savedOverdueWidths,
    onSave: handleSaveWidths,
    minWidth: 60,
    maxWidth: 400,
  });

  if (!isOpen) return null;

  const isPaymentsReady = title === 'Payments Ready' || title === t('kpiModal.paymentsReady');
  const isOverdueInvoices = title === t('kpiModal.invoicesOverdue') || title === 'Overdue Invoices' || title === 'Facturas Vencidas';

  const calculateDaysOverdue = (dueDate: string | null | undefined): number | null => {
    const days = daysOverdue(dueDate);
    return days > 0 ? days : null;
  };

  const getOverdueColorClass = (days: number | null): string => {
    if (days === null || days <= 0) return 'text-gray-600';
    if (days > 30) return 'bg-red-100 text-red-800';
    if (days > 14) return 'bg-orange-100 text-orange-800';
    return 'bg-yellow-100 text-yellow-800';
  };

  // Group campaigns by influencer for Payments Ready view
  const groupedByInfluencer: InfluencerPayments[] = isPaymentsReady
    ? (() => {
        const grouped = campaigns.reduce((acc, campaign) => {
          const influencer = campaign.influencer || campaign.client || 'Serviços';
          const stats = getInvoiceStats(campaign);
          const detail = getPayoutDetail(campaign, { netPayable: stats.netPayable });
          if (!acc[influencer]) {
            acc[influencer] = [];
          }
          acc[influencer].push({
            campaign,
            totalReceived: stats.totalReceived,
            netPayable: detail.amount,
            paidInvoices: stats.paidInvoices,
            totalInvoices: stats.totalInvoices,
            detail,
          });
          return acc;
        }, {} as Record<string, CampaignPaymentInfo[]>);

        return Object.entries(grouped)
          .filter(([, infos]) => infos.length > 0)
          .map(([influencer, campaignInfos]) => ({
            influencer,
            campaignInfos,
            totalAmount: campaignInfos.reduce((sum, info) => sum + info.netPayable, 0)
          })).sort((a, b) => a.influencer.localeCompare(b.influencer));
      })()
    : [];

  const toggleInfluencerExpanded = (influencer: string) => {
    setExpandedInfluencers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(influencer)) {
        newSet.delete(influencer);
      } else {
        newSet.add(influencer);
      }
      return newSet;
    });
  };

  // --- Selection logic ---

  const getSelection = (influencer: string): Set<string> => {
    return selectedCampaigns.get(influencer) || new Set();
  };

  const toggleCampaignSelection = (influencer: string, campaignId: string) => {
    setSelectedCampaigns(prev => {
      const next = new Map(prev);
      const current = new Set(next.get(influencer) || []);
      if (current.has(campaignId)) {
        current.delete(campaignId);
      } else {
        current.add(campaignId);
      }
      if (current.size === 0) {
        next.delete(influencer);
      } else {
        next.set(influencer, current);
      }
      return next;
    });
  };

  const toggleSelectAll = (influencer: string, campaignInfos: CampaignPaymentInfo[]) => {
    setSelectedCampaigns(prev => {
      const next = new Map(prev);
      const current = next.get(influencer) || new Set();
      const allIds = campaignInfos.map(i => i.campaign.id);
      const allSelected = allIds.every(id => current.has(id));
      if (allSelected) {
        next.delete(influencer);
      } else {
        next.set(influencer, new Set(allIds));
      }
      return next;
    });
  };

  const clearSelection = (influencer: string) => {
    setSelectedCampaigns(prev => {
      const next = new Map(prev);
      next.delete(influencer);
      return next;
    });
  };

  // --- Copy logic (respects selection) ---

  const copyInfluencerPayments = async (influencerPayment: InfluencerPayments) => {
    const sel = getSelection(influencerPayment.influencer);
    const infos = sel.size > 0
      ? influencerPayment.campaignInfos.filter(info => sel.has(info.campaign.id))
      : influencerPayment.campaignInfos;

    const message = buildCreatorPayoutEmail(
      influencerPayment.influencer,
      infos.map(info => ({ campaign: info.campaign, detail: info.detail }))
    );

    try {
      await navigator.clipboard.writeText(message);
      setCopiedInfluencer(influencerPayment.influencer);
      setTimeout(() => setCopiedInfluencer(null), 2000);
    } catch (error) {
      log.error('Failed to copy:', error);
    }
  };

  const handleMarkAsPaid = async (campaignId: string, amount: number, creatorName?: string) => {
    if (!onMarkAsPaid) return;
    setLoadingId(campaignId);
    try {
      await onMarkAsPaid(campaignId, amount, creatorName);
    } finally {
      setLoadingId(null);
      setConfirmingId(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-brand-ink/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-card shadow-float max-w-6xl w-full max-h-[90vh] flex flex-col animate-in zoom-in-95 slide-in-from-bottom-4 duration-300">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="mo-display text-lg text-brand-ink">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {projectos.length > 0 && (
            <div className="mb-5 space-y-2" data-testid="kpi-projectos">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Projectos</p>
              {projectos.map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => onOpenProjecto?.(p.projecto_id)}
                  className="w-full flex items-center justify-between gap-3 rounded-xl border border-gray-200 px-4 py-2.5 text-left text-sm hover:border-gray-400 transition-colors"
                >
                  <span className="font-medium text-gray-900 truncate">{p.label}</span>
                  <span className="font-semibold tabular-nums text-gray-900 whitespace-nowrap">{formatEuro(p.amount)}</span>
                </button>
              ))}
            </div>
          )}
          {campaigns.length === 0 ? (
            projectos.length === 0 && <p className="text-center text-gray-500 py-8">{t('kpiModal.noCampaigns')}</p>
          ) : isPaymentsReady ? (
            <div className="space-y-4">
              {groupedByInfluencer.map((influencerPayment) => {
                const isCopied = copiedInfluencer === influencerPayment.influencer;
                const isExpanded = expandedInfluencers.has(influencerPayment.influencer);
                const displayedInfos = isExpanded ? influencerPayment.campaignInfos : influencerPayment.campaignInfos.slice(0, 3);
                const hasMore = influencerPayment.campaignInfos.length > 3;
                const selection = getSelection(influencerPayment.influencer);
                const hasSelection = selection.size > 0;
                const selectedCount = hasSelection ? selection.size : influencerPayment.campaignInfos.length;
                const selectedTotal = hasSelection
                  ? influencerPayment.campaignInfos.filter(i => selection.has(i.campaign.id)).reduce((sum, i) => sum + i.netPayable, 0)
                  : influencerPayment.totalAmount;
                const allSelected = influencerPayment.campaignInfos.length > 0 &&
                  influencerPayment.campaignInfos.every(i => selection.has(i.campaign.id));
                const includedInfos = hasSelection
                  ? influencerPayment.campaignInfos.filter(i => selection.has(i.campaign.id))
                  : influencerPayment.campaignInfos;
                const expensesTotal = getExpensesTotalFor(includedInfos.map(i => i.campaign));

                return (
                  <div key={influencerPayment.influencer} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{influencerPayment.influencer}</h3>
                        <p className="text-xs text-gray-600 mt-0.5">
                          {hasSelection ? (
                            <>
                              <span className="font-semibold text-blue-700">{selectedCount} selecionada{selectedCount > 1 ? 's' : ''}</span>
                              {' '}de {influencerPayment.campaignInfos.length} •
                              <span className="font-semibold text-green-700"> {t('kpiModal.total')}: {formatEuro(selectedTotal)}</span>
                              {expensesTotal > 0 && (
                                <span className="font-medium text-red-600"> (inclui −{formatEuro(expensesTotal)} despesas)</span>
                              )}
                            </>
                          ) : (
                            <>
                              {influencerPayment.campaignInfos.length} {influencerPayment.campaignInfos.length > 1 ? t('kpiModal.campaigns') : t('kpiModal.campaign')} • {t('kpiModal.total')}: {formatEuro(influencerPayment.totalAmount)}
                              {expensesTotal > 0 && (
                                <span className="font-medium text-red-600"> (inclui −{formatEuro(expensesTotal)} despesas)</span>
                              )}
                            </>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {hasSelection && (
                          <button
                            onClick={() => clearSelection(influencerPayment.influencer)}
                            className="text-xs text-gray-500 hover:text-gray-700 underline underline-offset-2 transition-colors"
                          >
                            Limpar
                          </button>
                        )}
                        <button
                          onClick={() => copyInfluencerPayments(influencerPayment)}
                          className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                            isCopied
                              ? 'bg-brand-ink text-white'
                              : 'bg-brand-lime text-brand-ink hover:bg-brand-lime-hover'
                          }`}
                        >
                          {isCopied ? (
                            <>
                              <Check className="w-3.5 h-3.5" />
                              {t('kpiModal.copied')}
                            </>
                          ) : (
                            <>
                              <Copy className="w-3.5 h-3.5" />
                              {t('kpiModal.copyForEmail')}
                              {hasSelection && (
                                <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold bg-white/20 rounded">
                                  {selectedCount}
                                </span>
                              )}
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-center w-10">
                              <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={() => toggleSelectAll(influencerPayment.influencer, influencerPayment.campaignInfos)}
                                className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                title="Selecionar todas"
                              />
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('kpiModal.workCode')}
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('kpiModal.campaignRef')}
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('kpiModal.brand')}
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('kpiModal.monthOfDeliverable')}
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('kpiModal.netCreator')}
                            </th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('kpiModal.paymentParcel')}
                            </th>
                            {onMarkAsPaid && (
                              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                                Paid
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {displayedInfos.map((info) => {
                            const campaign = info.campaign;
                            const isConfirming = confirmingId === campaign.id;
                            const isRowLoading = loadingId === campaign.id;
                            const isSelected = selection.has(campaign.id);
                            return (
                              <tr
                                key={campaign.id}
                                className={`transition-colors ${
                                  isSelected
                                    ? 'bg-blue-50/60'
                                    : isConfirming
                                      ? 'bg-green-50/50'
                                      : 'hover:bg-gray-50'
                                }`}
                              >
                                <td className="px-3 py-2.5 text-center">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleCampaignSelection(influencerPayment.influencer, campaign.id)}
                                    className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </td>
                                <td className="px-3 py-2.5 whitespace-nowrap text-sm">
                                  {onEdit && campaign.work_code ? (
                                    <button
                                      onClick={() => {
                                        onEdit(campaign);
                                        onClose();
                                      }}
                                      className="text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                                      title="Edit campaign"
                                    >
                                      {campaign.work_code}
                                    </button>
                                  ) : (
                                    <span className="text-gray-900">{campaign.work_code || 'N/A'}</span>
                                  )}
                                </td>
                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-900">
                                  {campaign.campaign_ref || 'N/A'}
                                </td>
                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-900">
                                  {campaign.brand || campaign.client || 'N/A'}
                                </td>
                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-900">
                                  {info.detail.periodLabel}
                                </td>
                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-900 font-medium text-right">
                                  {formatEuro(info.netPayable)}
                                </td>
                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-center">
                                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                    {info.detail.parcelsLabel}
                                  </span>
                                </td>
                                {onMarkAsPaid && (
                                  <td className="px-3 py-2.5 whitespace-nowrap text-center">
                                    {isRowLoading ? (
                                      <Loader2 className="w-4 h-4 mx-auto text-green-600 animate-spin" />
                                    ) : isConfirming ? (
                                      <div className="flex items-center gap-1 justify-center">
                                        <button
                                          onClick={() => handleMarkAsPaid(campaign.id, info.netPayable, influencerPayment.influencer)}
                                          className="px-1.5 py-0.5 text-xs font-medium bg-brand-ink text-white rounded hover:bg-green-700 transition-colors"
                                        >
                                          Sim
                                        </button>
                                        <button
                                          onClick={() => setConfirmingId(null)}
                                          className="px-1.5 py-0.5 text-xs font-medium bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                                        >
                                          Nao
                                        </button>
                                      </div>
                                    ) : (
                                      <button
                                        onClick={() => setConfirmingId(campaign.id)}
                                        className="w-5 h-5 rounded border-2 border-gray-300 hover:border-green-500 transition-colors flex items-center justify-center mx-auto group"
                                        title={`Marcar como pago: ${formatEuro(info.netPayable)}`}
                                      >
                                        <Check className="w-3 h-3 text-transparent group-hover:text-green-400 transition-colors" />
                                      </button>
                                    )}
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {hasMore && (
                      <div className="bg-gray-50 px-4 py-2 border-t border-gray-200">
                        <button
                          onClick={() => toggleInfluencerExpanded(influencerPayment.influencer)}
                          className="w-full flex items-center justify-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                        >
                          {isExpanded ? (
                            <>
                              <ChevronUp className="w-4 h-4" />
                              {t('kpiModal.showLess')}
                            </>
                          ) : (
                            <>
                              <ChevronDown className="w-4 h-4" />
                              {influencerPayment.campaignInfos.length - 3 > 1
                                ? t('kpiModal.showMorePlural').replace('{0}', String(influencerPayment.campaignInfos.length - 3))
                                : t('kpiModal.showMore').replace('{0}', String(influencerPayment.campaignInfos.length - 3))
                              }
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : isOverdueInvoices ? (
            <div className="space-y-2">
              <div className="flex justify-end">
                <button
                  onClick={resetWidths}
                  className="flex items-center gap-1.5 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                  title="Reset column widths"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Reset
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="divide-y divide-gray-200" style={{ tableLayout: 'fixed', width: 'max-content', minWidth: '100%' }}>
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="group relative px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide" style={getColumnStyle('overdueWorkCode')}>
                        Work Code
                        <ResizeHandle columnKey="overdueWorkCode" onResizeStart={handleResizeStart} isResizing={resizingColumn === 'overdueWorkCode'} />
                      </th>
                      <th className="group relative px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide" style={getColumnStyle('overdueCampaignRef')}>
                        Campaign Ref
                        <ResizeHandle columnKey="overdueCampaignRef" onResizeStart={handleResizeStart} isResizing={resizingColumn === 'overdueCampaignRef'} />
                      </th>
                      <th className="group relative px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide" style={getColumnStyle('overdueInvoiceNumber')}>
                        Invoice No.
                        <ResizeHandle columnKey="overdueInvoiceNumber" onResizeStart={handleResizeStart} isResizing={resizingColumn === 'overdueInvoiceNumber'} />
                      </th>
                      <th className="group relative px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide" style={getColumnStyle('overdueClient')}>
                        Client
                        <ResizeHandle columnKey="overdueClient" onResizeStart={handleResizeStart} isResizing={resizingColumn === 'overdueClient'} />
                      </th>
                      <th className="group relative px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide" style={getColumnStyle('overdueBrand')}>
                        Brand
                        <ResizeHandle columnKey="overdueBrand" onResizeStart={handleResizeStart} isResizing={resizingColumn === 'overdueBrand'} />
                      </th>
                      <th className="group relative px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide" style={getColumnStyle('overdueDueDate')}>
                        Due Date
                        <ResizeHandle columnKey="overdueDueDate" onResizeStart={handleResizeStart} isResizing={resizingColumn === 'overdueDueDate'} />
                      </th>
                      <th className="group relative px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wide" style={getColumnStyle('overdueAmount')}>
                        Amount
                        <ResizeHandle columnKey="overdueAmount" onResizeStart={handleResizeStart} isResizing={resizingColumn === 'overdueAmount'} />
                      </th>
                      <th className="group relative px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wide" style={getColumnStyle('overdueDaysOverdue')}>
                        Dias em Atraso
                        <ResizeHandle columnKey="overdueDaysOverdue" onResizeStart={handleResizeStart} isResizing={resizingColumn === 'overdueDaysOverdue'} />
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {campaigns
                      .flatMap((campaign) => {
                        const overdueInvs = getOverdueInvoices(campaign);
                        return overdueInvs.map((inv, idx) => ({
                          campaign,
                          inv,
                          key: `${campaign.id}-${idx}`,
                        }));
                      })
                      .sort((a, b) => {
                        const daysA = calculateDaysOverdue(a.inv.dueDate) || 0;
                        const daysB = calculateDaysOverdue(b.inv.dueDate) || 0;
                        return daysB - daysA;
                      })
                      .map(({ campaign, inv, key }) => {
                        const daysOverdue = calculateDaysOverdue(inv.dueDate);
                        return (
                          <tr key={key} className="hover:bg-gray-50">
                            <td className="px-2 py-2 text-xs overflow-hidden text-ellipsis" style={getColumnStyle('overdueWorkCode')}>
                              {onEdit ? (
                                <button
                                  onClick={() => {
                                    onEdit(campaign);
                                    onClose();
                                  }}
                                  className="font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                                  title="Edit campaign"
                                >
                                  {campaign.work_code}
                                </button>
                              ) : (
                                <span className="font-medium text-gray-900">{campaign.work_code}</span>
                              )}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-600 overflow-hidden text-ellipsis" style={getColumnStyle('overdueCampaignRef')}>
                              {campaign.campaign_ref || '-'}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-700 font-mono overflow-hidden text-ellipsis" style={getColumnStyle('overdueInvoiceNumber')}>
                              {inv.invoiceNumber}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900 overflow-hidden text-ellipsis" style={getColumnStyle('overdueClient')}>
                              {campaign.client}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-500 overflow-hidden text-ellipsis" style={getColumnStyle('overdueBrand')}>
                              {campaign.brand || '-'}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900" style={getColumnStyle('overdueDueDate')}>
                              {new Date(inv.dueDate).toLocaleDateString('pt-PT')}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900 text-right font-medium" style={getColumnStyle('overdueAmount')}>
                              {formatEuro(inv.amount)}
                            </td>
                            <td className="px-2 py-2 text-xs text-center" style={getColumnStyle('overdueDaysOverdue')}>
                              {daysOverdue !== null ? (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${getOverdueColorClass(daysOverdue)}`}>
                                  {daysOverdue} {daysOverdue === 1 ? 'dia' : 'dias'}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Work Code
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Influencer
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Brand
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fee
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Delivery
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {campaigns.map((campaign) => (
                    <tr key={campaign.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {onEdit ? (
                          <button
                            onClick={() => {
                              onEdit(campaign);
                              onClose();
                            }}
                            className="font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                            title="Edit campaign"
                          >
                            {campaign.work_code}
                          </button>
                        ) : (
                          <span className="font-medium text-gray-900">{campaign.work_code}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {campaign.client}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {campaign.influencer}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {campaign.brand || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            campaign.campaign_status === 'Completed'
                              ? 'bg-green-100 text-green-800'
                              : campaign.campaign_status === 'Billing'
                              ? 'bg-amber-100 text-amber-800'
                              : campaign.campaign_status === 'In Progress'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {campaign.campaign_status || 'Consultation'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        €{campaign.fee.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            campaign.delivery_status === 'Published'
                              ? 'bg-green-100 text-green-800'
                              : campaign.delivery_status === 'Approved'
                              ? 'bg-teal-100 text-teal-800'
                              : campaign.delivery_status === 'Delivered'
                              ? 'bg-indigo-100 text-indigo-800'
                              : campaign.delivery_status === 'Production'
                              ? 'bg-blue-100 text-blue-800'
                              : campaign.delivery_status === 'Rejected'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {campaign.delivery_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center gap-6">
            <p className="text-sm text-gray-600">
              {t('kpiModal.total')}: <span className="font-semibold">{campaigns.length}</span> {campaigns.length !== 1 ? t('kpiModal.campaigns') : t('kpiModal.campaign')}
            </p>
            {isOverdueInvoices && (() => {
              const overdueTotal = campaigns.reduce((sum, c) => {
                const rows = getOverdueInvoices(c);
                return sum + rows.reduce((s, r) => s + r.amount, 0);
              }, 0);
              return (
                <p className="text-sm text-gray-600">
                  Total em atraso: <span className="font-semibold text-red-600">{formatEuro(overdueTotal)}</span>
                </p>
              );
            })()}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            {t('kpiModal.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
